import React from 'react';

import {assert} from 'Asserts';
import * as contextmenu from 'components/ContextMenu';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {
  concentrationUnitTypePredicate,
  containerQuantityUnitTypePredicate,
} from 'inventory/InventoryUnitUtil';
import {
  ConcentrationStructuredTableColumn,
  QuantityStructuredTableColumn,
} from 'notebook/structured-tables/StructuredTableConstants';
/* eslint-disable import/order */
import {UnitsColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {ColumnConstraint, SelectionRange, TableColumnType} from 'notebook/tables/TableConstants';
import TableAxisCell, {TableAxisCellProps} from 'notebook/tables/core-ui/TableAxisCell';
import {UNIT_TYPES_QUERY} from 'units/UnitsConstants';
import {UnitTypesQuery} from 'units/__generated/UnitTypesQuery';
/* eslint-enable import/order */

export interface UnitsColumnConstraint extends ColumnConstraint {
  columnDefinition: UnitsColumnDefinition;
  structuredTableColumn: ConcentrationStructuredTableColumn | QuantityStructuredTableColumn;
}

function isUnitsColumnConstraint(
  columnConstraint: ColumnConstraint
): columnConstraint is UnitsColumnConstraint {
  return (
    columnConstraint.columnDefinition?.type === TableColumnType.QUANTITY ||
    columnConstraint.columnDefinition?.type === TableColumnType.CONCENTRATION
  );
}

function getUpdatedColumnConstraint(
  columnConstraint: UnitsColumnConstraint,
  newUnit: {symbol: string; id?: string}
): UnitsColumnConstraint {
  return {
    ...columnConstraint,
    columnDefinition: {
      ...columnConstraint.columnDefinition,
      unitApiIdentifier: newUnit.id,
      units: newUnit.symbol,
    },
    structuredTableColumn: {
      ...columnConstraint.structuredTableColumn,
      unitApiIdentifier: newUnit.id,
      units: newUnit.symbol,
    },
  };
}

type ClickHandler = (newUnit: {symbol: string; id?: string}) => void;

// For usability we want to ensure that certain common unit types are listed first
const ordering = ['Molar concentration', 'Mass concentration', 'Volume', 'Mass', 'Amount of substance'];
const compareUnitTypes = (a: string, b: string): number =>
  (ordering.indexOf(a) < 0 ? 5 : ordering.indexOf(a)) - (ordering.indexOf(b) < 0 ? 5 : ordering.indexOf(b));

function makeUnitDictionaryMenuOptions(
  allUnitTypes: UnitTypesQuery['unitTypes'],
  columnType: TableColumnType,
  onClick: ClickHandler
): Array<contextmenu.ContextMenuOption> {
  const filterPredicate =
    columnType === TableColumnType.CONCENTRATION
      ? concentrationUnitTypePredicate
      : containerQuantityUnitTypePredicate;
  const unitTypes = allUnitTypes.filter((ut) => filterPredicate(ut) && !ut.archiveData.archived);

  const options: Array<contextmenu.ContextMenuOption> = [];
  unitTypes
    .sort((a, b) => compareUnitTypes(a.name, b.name))
    .forEach((ut) => {
      options.push({
        name: ut.name,
        disabled: true,
      });
      ut.units
        .filter((u) => !u.archiveData.archived)
        .forEach((u) => {
          options.push({
            name: u.symbol,
            id: u.id,
            click: () => {
              onClick({symbol: u.symbol, id: u.id});
            },
          });
        });
    });
  return options;
}

function UnitsTableAxisCell(props: TableAxisCellProps): JSX.Element {
  const unitTypesResponse = useSkippableBenchlingQuery<UnitTypesQuery>(UNIT_TYPES_QUERY, {
    variables: {},
  });

  const columnConstraint = props.columnConstraint;
  if (!isUnitsColumnConstraint(columnConstraint!)) {
    assert(false, 'UnitsTableAxisCell called without a UnitsColumnConstraint');
  }
  const onClick = (newUnit: {symbol: string; id?: string}): void => {
    if (props.setColumnConstraint) {
      props.setColumnConstraint(props.index, getUpdatedColumnConstraint(columnConstraint, newUnit), {});
    }
  };

  const columnDefinition = columnConstraint.columnDefinition;
  const showMenu = !columnDefinition?.hideContextMenu && !props.locked;

  let unitsMenuOptions: Array<contextmenu.ContextMenuOption> = [];
  if (showMenu && columnDefinition && unitTypesResponse.type === QueryResultType.SUCCESS) {
    unitsMenuOptions = makeUnitDictionaryMenuOptions(
      unitTypesResponse.data.unitTypes,
      columnDefinition.type,
      onClick
    );
  }

  const wrappedOptions = unitsMenuOptions.length ? [{name: 'Units', menu: unitsMenuOptions}] : [];

  const getExtraContextMenuOptions = (
    selectionRange: SelectionRange | null
  ): Array<contextmenu.ContextMenuOption> | null => {
    const inheritedMenuOptions = props.getExtraContextMenuOptions?.(selectionRange) ?? [];
    return [...inheritedMenuOptions, ...wrappedOptions];
  };

  return <TableAxisCell {...props} getExtraContextMenuOptions={getExtraContextMenuOptions} />;
}

export default UnitsTableAxisCell;
